import { defineMessages, useIntl } from 'react-intl';

import { CoughScanDisease, CoughScanField } from '@constants/scan';

const messages = defineMessages({
  'asthma.text': {
    id: 'coughScanDrilldown.asthma.text',
    defaultMessage: 'Asthma',
  },
  'asthma.description': {
    id: 'coughScanDrilldown.asthma.description',
    defaultMessage:
      'Asthma is a chronic respiratory disease characterized by inflammation and narrowing of the airways, leading to breathing difficulties. Common symptoms include wheezing, shortness of breath, chest tightness, and coughing, especially at night or early morning. Asthma can be triggered by various factors, such as allergies, air pollution, and physical activity.',
  },
  'bronchiectasis.text': {
    id: 'coughScanDrilldown.bronchiectasis.text',
    defaultMessage: 'Bronchiectasis',
  },
  'bronchiectasis.description': {
    id: 'coughScanDrilldown.bronchiectasis.description',
    defaultMessage:
      'Bronchiectasis is a disease in which there is permanent enlargement of parts of the airways of the lung. Symptoms typically include a chronic cough with mucus production. Other symptoms include shortness of breath, coughing up blood, and chest pain. Wheezing and nail clubbing may also occur.',
  },
  'copd.text': {
    id: 'coughScanDrilldown.copd.text',
    defaultMessage: 'Chronic Obstructive Pulmonary Disease (COPD)',
  },
  'copd.description': {
    id: 'coughScanDrilldown.copd.description',
    defaultMessage:
      'COPD is a group of lung diseases that makes it difficult to breathe and can cause coughing, wheezing, and shortness of breath. It is often caused by long-term exposure to lung irritants, such as tobacco smoke, air pollution, and secondhand smoke.',
  },
  'covid.text': {
    id: 'coughScanDrilldown.covid.text',
    defaultMessage: 'COVID-19',
  },
  'covid.description': {
    id: 'coughScanDrilldown.covid.description',
    defaultMessage:
      'COVID-19 is a highly infectious respiratory disease caused by the SARS-CoV-2 virus. It spreads mainly through respiratory droplets when an infected person talks, coughs, or sneezes, or by touching a surface contaminated with the virus and then touching the mouth, nose, or eyes. Symptoms of COVID-19 may include fever, cough, shortness of breath, fatigue, muscle aches, loss of taste or smell, sore throat, and congestion or runny nose. The severity of the illness can range from mild to severe, and in some cases, it can be fatal, especially in older adults or people with underlying health conditions. Vaccines and other preventive measures, such as wearing masks, social distancing, and practicing good hand hygiene, can help prevent the spread of COVID-19.',
  },
  'ild.text': {
    id: 'coughScanDrilldown.ild.text',
    defaultMessage: 'Interstitial Lung Disease (ILD)',
  },
  'ild.description': {
    id: 'coughScanDrilldown.ild.description',
    defaultMessage:
      'ILD is a general term used to describe a group of lung diseases that affect the interstitial tissue (the tissue surrounding the air sacs) in the lungs. ILD is a progressive disease that can make it difficult to breathe and can cause coughing, wheezing, and shortness of breath.',
  },
  'pneumonia.text': {
    id: 'coughScanDrilldown.pneumonia.text',
    defaultMessage: 'Pneumonia',
  },
  'pneumonia.description': {
    id: 'coughScanDrilldown.pneumonia.description',
    defaultMessage:
      'Pneumonia is a lung infection that can be caused by a variety of germs, including bacteria, viruses, and fungi. It causes inflammation and fluid buildup in the air sacs of the lungs, making it difficult to breathe and leading to symptoms such as cough, fever, chills, chest pain, and shortness of breath.',
  },
  'tuberculosis.text': {
    id: 'coughScanDrilldown.tuberculosis.text',
    defaultMessage: 'Tuberculosis',
  },
  'tuberculosis.description': {
    id: 'coughScanDrilldown.tuberculosis.description',
    defaultMessage:
      'Tuberculosis (TB) is a bacterial infection that primarily affects the lungs, but can also spread to other parts of the body. TB is spread from person to person through the air when an infected person coughs or sneezes. The symptoms of TB can include a persistent cough, fever, night sweats, weight loss, and fatigue.',
  },
});

export default function useCoughScan() {
  const { formatMessage } = useIntl();

  // Descriptions from https://www.helfie.ai/cough-ai (excluding bronchiectasis - Wikipedia)
  const coughScanMap: Map<
    CoughScanDisease,
    Map<CoughScanField, string>
  > = new Map(
    Object.values(CoughScanDisease).map((disease) => [
      disease,
      new Map([
        [CoughScanField.Text, formatMessage(messages[`${disease}.text`])],
        [
          CoughScanField.Description,
          formatMessage(messages[`${disease}.description`]),
        ],
      ]),
    ])
  );

  return { coughScanMap };
}
